import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import filterContext from "../../../app/providers/filter/filterContext";
import { 
    Box, 
    Typography, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions,
    IconButton,
    Alert,
    Button
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { greyFontColor } from "../../../shared/styles/fontStyles";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useWindowSize } from "../../../shared/hooks/useWindowSize";
import { dialogStyle } from "../../../shared/styles/dialogStyles";
import { altChooseButtonStyleTurquoise, altCloseButtonStyleDark } from "../../../shared/styles/buttonStyles";
import { useDispatch, useSelector } from "react-redux";
import { setDraft } from "../../../app/store/reservationSlice";

export default function ServiceFilter() {

    const { t, i18n } = useTranslation();
    const { isMobile } = useWindowSize();

    const { state : filterState, setFilter } = useContext(filterContext);
    const [ basicServices, setBasicServices ] = useState([]);
    const [ extraServices, setExtraServices ] = useState([]);
    const [ servicesLoading, setServicesLoading ] = useState(true);

    // Local state for temporary selections before applying
    const [ tempBasicServices, setTempBasicServices ] = useState([]);
    const [ tempExtraServices, setTempExtraServices ] = useState([]);

    const [ dialogOpen, setDialogOpen ] = useState(false);
    const dispatch = useDispatch();
    const draft = useSelector((state) => state.reservation.draft);
    const handleDialogOpen = () => {
        // Initialize temp state with current selections when opening dialog
        setTempBasicServices([...basicServices]);
        setTempExtraServices([...extraServices]);
        setDialogOpen(true);
    }

    const handleDialogClose = () => {
        setDialogOpen(false);
    }

    const handleApply = () => {
        // Apply the temporary selections to the actual filter context
        const allTempServices = [...tempBasicServices, ...tempExtraServices];
        
        const updatedFilter = filterState.filter.map(filterGroup => {
            if (filterGroup.headerName === 'availableServices') {
                return {
                    ...filterGroup,
                    services: allTempServices
                };
            }
            return filterGroup;
        });
        const activeServices = allTempServices.filter(service => service.active);
        const updatedDraftServices = activeServices.filter((service, index, self) =>
            index === self.findIndex((s) => s.id === service.id)
        );
        dispatch(setDraft({ services: updatedDraftServices }));
        setFilter(updatedFilter);
        setDialogOpen(false);
    }

    const handleServiceClick = (serviceId) => {
        // Toggle active state in temporary local state
        setTempBasicServices(prev => 
            prev.map(service => 
                service.id === serviceId 
                    ? { ...service, active: !service.active }
                    : service
            )
        );
        
        setTempExtraServices(prev => 
            prev.map(service => 
                service.id === serviceId 
                    ? { ...service, active: !service.active }
                    : service
            )
        );
    };

    useEffect(() => {
        const createServiceList = () => {
            if (Array.isArray(filterState.filter)) {
                let allServices = filterState.filter.filter(filter => filter.headerName === 'availableServices')[0]?.services || [];
                const isAnyActive = allServices.some(service => service.active);
                if(draft.services && draft.services.length > 0) {
                    const selectedServiceIds = draft.services.map(service => service.id);
                    allServices = allServices.map(service => {
                        if(selectedServiceIds.includes(service.id)){
                            return {...service, active: true}
                        }
                        return {...service, active: false}
                    })
                }else{
                    if(!isAnyActive){ // If no services are active
                        allServices = allServices.map(service => {
                            if(service.preSelect){ // Check if the service should be pre-selected
                                return {...service, active: true}
                            }
                            return {...service, active: false}
                        })
                    }
                }
                // Split services into basic and extra based on level
                const basic = allServices.filter(service => service.level === 'basic');
                const extra = allServices.filter(service => service.level === 'extra');
                
                setBasicServices(basic);
                setExtraServices(extra);
                setServicesLoading(false);
            }
        };
        createServiceList();
    }, [filterState.filter, draft.services]);

    const getDisplayText = () => {
        const allServices = [...basicServices, ...extraServices];
        const activeServices = allServices.filter(service => service.active);
        
        if (activeServices.length === 0) {
            return t("filter.noSelectedServices");
        } else if (activeServices.length === 1) {
            return activeServices[0].translations[i18n.language]?.name || activeServices[0].translations.fi.name;
        } else {
            const firstName = activeServices[0].translations[i18n.language]?.name || activeServices[0].translations.fi.name;
            const remainingCount = activeServices.length - 1;
            return `${firstName} +${remainingCount}`;
        }
    };

    
    const serviceFontStyleHeader = {
        fontWeight: 500,
        fontSize: '0.875rem'
    }

    const serviceFontStyleDescription = {
        opacity: 0.8,
        mt: 0.5,
        fontSize: '0.75rem'
    }

    const fontColor = {
        color: '#003C59'
    }

    while (servicesLoading) {
        return null;
    }

    // color: '#1976d2' This was moved up from Typography below (getDisplayText)
    //  sx={{ color: '#1976d2' }} this was moved up from ExapandMoreIcon

    return (
        <>
            <Box
                onClick={handleDialogOpen}
                sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    padding: '12px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    backgroundColor: 'white',
                    '&:hover': {
                        backgroundColor: '#f5f5f5'
                    }
                }}
            >
                <Typography variant="body1" sx={{ ...fontColor, fontWeight: 700, fontSize: '0.775rem' }}>
                    {getDisplayText()}
                </Typography>
                <ExpandMoreIcon />
            </Box>

            <Dialog
                open={dialogOpen}
                onClose={handleDialogClose}
                maxWidth="sm"
                fullWidth
                sx={{...dialogStyle}}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: isMobile ? "flex-end" : 'space-between', alignItems: 'flex-start' }}>
                    {!isMobile && <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', ...fontColor }}>
                            {t("filter.availableServices")}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', marginTop: 1, ...fontColor }}>
                            {t("filter.availableServicesDescription")}
                        </Typography>
                    </Box>}
                    <IconButton onClick={handleDialogClose} sx={{ padding: 0, marginTop: '-4px' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ padding: isMobile ? '16px' : '36px' }}>
                    {isMobile && <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', ...fontColor }}>
                            {t("filter.availableServices")}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', marginTop: 1, ...fontColor }}>
                            {t("filter.availableServicesDescription")}
                        </Typography>
                    </Box>}
                    {/* Basic Services */}
                    {tempBasicServices.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" sx={{...fontColor, mb: 2, fontWeight: 'bold' }}>
                                {t("filter.baseServices")}
                            </Typography>
                            {tempBasicServices.map((service) => (
                                <Alert
                                    key={service.id}
                                    icon={service.active ? <CheckCircleOutlineIcon /> : <RadioButtonUncheckedIcon />}
                                    sx={{
                                        backgroundColor: service.active ? 'rgb(214, 252, 255)' : 'transparent',
                                        color: '#003C59',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '4px',
                                        marginBottom: '12px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: service.active ? 'rgb(150, 229, 242)' : '#f5f5f5',
                                        },
                                        '& .MuiAlert-icon': {
                                            color: '#003C59',
                                            alignSelf: 'center'
                                        },
                                        '& .MuiAlert-message': {
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'flex-start',
                                            width: '100%'
                                        }
                                    }}
                                    onClick={() => handleServiceClick(service.id)}
                                >
                                    <Typography variant="body1" sx={serviceFontStyleHeader}>
                                        {service.translations[i18n.language]?.name || service.translations.fi.name}
                                    </Typography>
                                    <Typography variant="body2" sx={serviceFontStyleDescription}>
                                        {service.translations[i18n.language]?.description || service.translations.fi.description}
                                    </Typography>
                                </Alert>
                            ))}
                        </Box>
                    )}
                    
                    {/* Extra Services */}
                    {tempExtraServices.length > 0 && (
                        <Box sx={{ marginTop: '38px' }}>
                            <Typography variant="h6" sx={{...fontColor, mb: 2, fontWeight: 'bold' }}>
                                {t("filter.uncommonServices")}
                            </Typography>
                            {tempExtraServices.map((service) => (
                                <Alert
                                    key={service.id}
                                    icon={service.active ? <CheckCircleOutlineIcon /> : <RadioButtonUncheckedIcon />}
                                    sx={{
                                        backgroundColor: service.active ? 'rgb(214, 252, 255)' : 'transparent',
                                        color: '#003C59',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '4px',
                                        marginBottom: '12px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: service.active ? 'rgb(150, 229, 242)' : '#f5f5f5',
                                        },
                                        '& .MuiAlert-icon': {
                                            color: '#003C59',
                                            alignSelf: 'center'
                                        },
                                        '& .MuiAlert-message': {
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'flex-start',
                                            width: '100%'
                                        }
                                    }}
                                    onClick={() => handleServiceClick(service.id)}
                                >
                                    <Typography variant="body1" sx={serviceFontStyleHeader}>
                                        {service.translations[i18n.language]?.name || service.translations.fi.name}
                                    </Typography>
                                    <Typography variant="body2" sx={serviceFontStyleDescription}>
                                        {service.translations[i18n.language]?.description || service.translations.fi.description}
                                    </Typography>
                                </Alert>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions 
                    sx={{ 
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: '#003C59',
                        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                        padding: '16px 24px',
                        gap: 2,
                        zIndex: 1
                    }}
                >
                    <Button 
                        onClick={handleDialogClose}
                        variant="outlined"
                        // sx={{ 
                        //     flex: 1,
                        //     color: 'white',
                        //     borderColor: 'rgba(255, 255, 255, 0.3)',
                        //     '&:hover': {
                        //         borderColor: 'white',
                        //         backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        //     }
                        // }}
                        sx={{...altCloseButtonStyleDark, flex: 1}}
                    >
                        {t("common.close")}
                    </Button>
                    <Button 
                        onClick={handleApply}
                        variant="contained"
                        sx={{...altChooseButtonStyleTurquoise, flex: 1 }}
                    >
                        {t("common.apply")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}